import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { useUserSettings } from '../../context/UserSettingsContext';
import api from '../../services/api';

interface ProductSelectionDialogProps {
  open: boolean;
  onComplete: () => void;
}

const AVAILABLE_PRODUCTS = [
  { id: 'Shell and tube heat exchanger', label: 'Shell and tube heat exchanger' },
  { id: 'Cold plates', label: 'Cold plates' },
  { id: 'Coaxial heat exchangers', label: 'Coaxial heat exchangers' },
];

const ProductSelectionDialog: React.FC<ProductSelectionDialogProps> = ({ open, onComplete }) => {
  const { settings, updateSettings } = useUserSettings();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize selected products only once when dialog opens or settings change
  useEffect(() => {
    if (open && !isInitialized && settings.interested_products) {
      setSelectedProducts(settings.interested_products);
      setIsInitialized(true);
    }
  }, [open, settings.interested_products, isInitialized]);

  // Reset initialization when dialog closes
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  const handleProductToggle = useCallback((productId: string) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedProducts.length === 0) return;

    setIsSubmitting(true);
    try {
      // Step 1: Update user settings with selected products and mark mail as sent
      await updateSettings({
        interested_products: selectedProducts,
        registration_mail_sent: true
      });

      // Step 2: Send registration mail
      await api.get('/users/send-registration-mail');

      // Step 3: Complete the dialog
      onComplete();
    } catch (error) {
      console.error('Error in product selection submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedProducts, updateSettings, onComplete]);

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Welcome to ThermalX
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Please select the products you are interested in:
          </Typography>
        </Box>
        <FormGroup>
          {AVAILABLE_PRODUCTS.map((product) => (
            <FormControlLabel
              key={product.id}
              control={
                <Checkbox
                  checked={selectedProducts.includes(product.id)}
                  onChange={() => handleProductToggle(product.id)}
                />
              }
              label={product.label}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={selectedProducts.length === 0 || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelectionDialog; 